import { ChannelIcon } from '../components/channel-icon.js';
import type { ConversationSummary, InboxMessage } from '@dnx/contracts';
import { Alert, Badge, Button, cn } from '@dnx/ui';
import type { InboxFilter } from '@dnx/contracts';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { AiRunSummary } from '@dnx/contracts';
import { AlertCircle, ArrowRightLeft, Bot, Check, CheckCheck, Clock, Hand, LockKeyhole, SendHorizontal, Sparkles, Undo2, X, Zap } from 'lucide-react';
import { type KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { QuickReplyMenu, type QuickReplyMenuHandle } from '../reception/quick-replies.js';
import { TagChips } from '../reception/tags.js';
import { TransferDialog } from '../reception/transfer-dialog.js';
import { clock, conversationQuery, inboxKeys, messagesQuery, relTime, waitTone } from './queries.js';

const drafts = new Map<string, string>(); // rascunho por conversa (docs/04 §5.2), só em memória

function StatusIcon({ status }: { status: InboxMessage['status'] }) {
  const map = {
    queued: <Clock className="size-3.5" aria-label="Enviando" />,
    sent: <Check className="size-3.5" aria-label="Enviada" />,
    delivered: <CheckCheck className="size-3.5" aria-label="Entregue" />,
    read: <CheckCheck className="size-3.5 text-info" aria-label="Lida" />,
    failed: <AlertCircle className="size-3.5 text-danger" aria-label="Falhou" />,
    received: null,
  } as const;
  return map[status];
}

const OUTCOME: Record<string, string> = {
  replied: 'Respondeu', handoff: 'Passou para a equipe', blocked: 'Resposta bloqueada pela verificação', error: 'Falhou', skipped: 'Não respondeu',
};
const TOOL_LABEL: Record<string, string> = {
  search_knowledge_base: 'Consultou a base de conhecimento', list_available_slots: 'Buscou horários livres',
  book_evaluation: 'Agendou avaliação', set_interest: 'Registrou o interesse',
};

/** Transparência para a equipe: o que a IA consultou e fez para escrever esta mensagem. */
function AiRunDetails({ runId }: { runId: string }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ['ai', 'run', runId], queryFn: () => api<AiRunSummary>(`/ai/runs/${runId}`), enabled: open, staleTime: Infinity });
  return (
    <div className="mt-1.5 border-t border-primary/15 pt-1.5 text-[11.5px]">
      <button type="button" onClick={() => setOpen((v) => !v)} className="font-medium text-primary hover:underline">
        {open ? 'Ocultar detalhes' : 'Por que esta resposta?'}
      </button>
      {open && data && (
        <div className="mt-1.5 space-y-1 text-fg-muted">
          <p>{OUTCOME[data.outcome]}{data.reason ? ` · ${data.reason}` : ''} · {((data.latencyMs ?? 0) / 1000).toFixed(1)} s</p>
          {data.tools.length > 0 && <p>{[...new Set(data.tools.map((t) => TOOL_LABEL[t.name] ?? t.name))].join(' · ')}</p>}
          {data.citations.length > 0 && <p>Fontes: {data.citations.map((c) => c.title).join(', ')}</p>}
        </div>
      )}
    </div>
  );
}

function Bubble({ m }: { m: InboxMessage }) {
  if (m.senderType === 'system') {
    return <p className="my-2 text-center text-[11.5px] text-fg-subtle">{m.text} · {clock(m.createdAt)}</p>;
  }
  const out = m.direction === 'out';
  return (
    <div className={cn('flex', out ? 'justify-end' : 'justify-start')}>
      <div className={cn('max-w-[75%] rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed shadow-card',
        out ? (m.senderType === 'automation' ? 'rounded-br-md border border-dashed border-primary/30 bg-surface text-fg'
          : m.senderType === 'ai_agent' ? 'rounded-br-md border border-primary/20 bg-primary-soft/60 text-fg' : 'rounded-br-md bg-primary-soft text-fg')
          : 'rounded-bl-md border border-border bg-surface text-fg',
        m.status === 'failed' && 'ring-1 ring-danger')}>
        {out && m.senderName && <p className="mb-0.5 text-[11px] font-semibold text-primary">{m.senderName}</p>}
        {out && m.senderType === 'phone' && <p className="mb-0.5 text-[11px] font-semibold text-fg-muted">Pelo celular da clínica</p>}
        {out && m.senderType === 'automation' && <p className="mb-0.5 text-[11px] font-semibold text-fg-muted">Mensagem automática</p>}
        {out && m.senderType === 'ai_agent' && <p className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-primary"><Sparkles className="size-3" />Assistente virtual</p>}
        <p className="whitespace-pre-wrap break-words">{m.text ?? <em className="text-fg-muted">Mensagem sem texto</em>}</p>
        <p className={cn('mt-1 flex items-center justify-end gap-1 text-[10.5px]', m.status === 'failed' ? 'text-danger' : 'text-fg-subtle')}>
          {m.status === 'failed' && 'Não enviada · '}{clock(m.createdAt)} {out && <StatusIcon status={m.status} />}
        </p>
        {m.aiRunId && <AiRunDetails runId={m.aiRunId} />}
      </div>
    </div>
  );
}

function dayLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === new Date(Date.now() - 86_400_000).toDateString()) return 'Ontem';
  return new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', timeZone: 'America/Sao_Paulo' }).format(d);
}

/** Estado de quem responde — o elemento mais visível da conversa (docs/04 §5.2). */
function Ownership({ c, meId, now }: { c: ConversationSummary; meId: string; now: number }) {
  if (c.status === 'closed') return <Badge tone="neutral">Encerrada</Badge>;
  if (c.handledBy === 'human' && c.assignedTo?.id === meId) return <Badge tone="success"><Hand className="size-3" />Com você</Badge>;
  if (c.handledBy === 'human' && c.assignedTo) return <Badge tone="primary">Com {c.assignedTo.name}</Badge>;
  if (c.handledBy === 'ai') return <Badge tone="primary"><Bot className="size-3" />Assistente virtual respondendo</Badge>;
  const tone = waitTone(c.waitingSince, now);
  return (
    <Badge tone={tone === 'late' ? 'danger' : 'warning'}>
      <Clock className="size-3" />
      {!c.waitingSince ? 'Na fila' : relTime(c.waitingSince, now) === 'agora' ? 'Aguardando atendimento' : `Aguardando ${relTime(c.waitingSince, now)}`}
    </Badge>
  );
}

export function ConversationView({ id, meId, now, filter }: { id: string; meId: string; now: number; filter: InboxFilter }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: c } = useQuery(conversationQuery(id));
  const { data: msgs } = useQuery(messagesQuery(id));
  const [text, setText] = useState(() => drafts.get(id) ?? '');
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const menu = useRef<QuickReplyMenuHandle>(null);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const { data: me } = useSuspenseQuery(meQuery);
  const canTransfer = me.permissions.includes('inbox.conversation.takeover');
  const canReply = me.permissions.includes('inbox.conversation.reply');

  useEffect(() => { setText(drafts.get(id) ?? ''); }, [id]);
  useEffect(() => { drafts.set(id, text); }, [id, text]);
  useLayoutEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight }); }, [msgs?.length, id]);
  useEffect(() => {
    if (c && c.unreadCount > 0) void api(`/inbox/conversations/${id}/read`, { method: 'POST' });
  }, [c, id]);

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: inboxKeys.conversation(id) }),
    qc.invalidateQueries({ queryKey: inboxKeys.messages(id) }),
    qc.invalidateQueries({ queryKey: ['inbox', 'list'] }),
    qc.invalidateQueries({ queryKey: inboxKeys.counts }),
  ]);
  const action = useMutation({
    mutationFn: (a: 'takeover' | 'release' | 'close') => api<void>(`/inbox/conversations/${id}/${a}`, { method: 'POST' }),
    onSuccess: async (_r, a) => {
      await refresh();
      // a conversa sai de "Aguardando" ao ser assumida: acompanha para "Minhas", mantendo-a aberta
      if (a === 'takeover' && filter === 'waiting') void navigate({ to: '/conversas', search: { filtro: 'mine', c: id }, replace: true });
    },
  });
  const send = useMutation({
    mutationFn: (t: string) => api<InboxMessage>(`/inbox/conversations/${id}/messages`, { method: 'POST', body: { text: t } }),
    onMutate: async (t) => {
      // otimista: a mensagem aparece na hora com o relógio de "enviando"
      await qc.cancelQueries({ queryKey: inboxKeys.messages(id) });
      const prev = qc.getQueryData<InboxMessage[]>(inboxKeys.messages(id));
      qc.setQueryData<InboxMessage[]>(inboxKeys.messages(id), [...(prev ?? []), {
        id: `tmp-${Date.now()}`, direction: 'out', senderType: 'user', senderName: 'Você', text: t, status: 'queued', createdAt: new Date().toISOString(),
      }]);
      setText('');
      return { prev, t };
    },
    onError: (_e, _t, ctx) => { if (ctx) { qc.setQueryData(inboxKeys.messages(id), ctx.prev); setText(ctx.t); } },
    onSettled: async () => {
      await refresh();
      if (filter === 'waiting') void navigate({ to: '/conversas', search: { filtro: 'mine', c: id }, replace: true });
    },
  });

  if (!c) return <div className="grid h-full place-items-center text-sm text-fg-muted">Carregando…</div>;
  const mine = c.handledBy === 'human' && c.assignedTo?.id === meId;
  const other = c.handledBy === 'human' && !mine;
  const canWrite = c.status === 'open' && !other;
  const submit = () => { const t = text.trim(); if (t && canWrite && !send.isPending) send.mutate(t); };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (menu.current?.onKey(e)) { e.preventDefault(); return; } // lista de respostas rápidas aberta
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); }
  };
  const vars = {
    nome: c.contact.name.split(' ')[0] ?? '',
    clinica: me.organizations.find((o) => o.organizationId === me.activeOrganizationId)?.tradeName ?? '',
    atendente: me.user.name.split(' ')[0] ?? '',
  };
  const pickReply = (t: string) => { setText(t); setRepliesOpen(false); requestAnimationFrame(() => input.current?.focus()); };

  const windowClosed = c.status === 'open' && c.channel.kind === 'instagram' && (!c.replyUntil || Date.parse(c.replyUntil) < now);
  let lastDay = '';
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface px-5 py-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-fg">{c.contact.name}</p>
          <p className="flex items-center gap-1.5 text-[12px] text-fg-muted">
            <ChannelIcon kind={c.channel.kind} className={cn('size-3.5', c.channel.kind === 'instagram' ? 'text-[#C13584]' : 'text-[#1FA855]')} />
            {[c.contact.instagram, c.contact.maskedPhone, c.channel.displayName].filter(Boolean).join(' · ')}
          </p>
          <TagChips tags={c.tags} className="mt-1" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Ownership c={c} meId={meId} now={now} />
          {c.status === 'open' && !mine && (
            <Button size="sm" loading={action.isPending && action.variables === 'takeover'} onClick={() => action.mutate('takeover')}>
              <Hand className="size-4" />{other ? 'Assumir mesmo assim' : 'Assumir conversa'}
            </Button>
          )}
          {mine && <Button size="sm" variant="secondary" onClick={() => action.mutate('release')}><Undo2 className="size-4" />Devolver à fila</Button>}
          {c.status === 'open' && canTransfer && (
            <Button size="sm" variant="ghost" onClick={() => setTransferring(true)}><ArrowRightLeft className="size-4" />Transferir</Button>
          )}
          {c.status === 'open' && <Button size="sm" variant="ghost" onClick={() => action.mutate('close')}><X className="size-4" />Encerrar</Button>}
        </div>
      </header>
      {action.error && <Alert tone="danger" className="m-3">{action.error.message}</Alert>}
      {transferring && <TransferDialog conversationId={id} currentUserId={c.assignedTo?.id ?? null} onClose={() => setTransferring(false)}
        onDone={() => void refresh()} />}

      <div ref={scroller} className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-bg px-5 py-4" aria-live="polite">
        {msgs?.map((m) => {
          const day = dayLabel(m.createdAt);
          const sep = day !== lastDay;
          lastDay = day;
          return (
            <div key={m.id}>
              {sep && <p className="my-3 text-center"><span className="rounded-full bg-surface-muted px-3 py-1 text-[11px] font-medium text-fg-muted">{day}</span></p>}
              <Bubble m={m} />
            </div>
          );
        })}
      </div>

      <div className="border-t border-border bg-surface p-3">
        {send.error && <Alert tone="danger" className="mb-2">{send.error.message}</Alert>}
        {other ? (
          <p className="flex items-center gap-2 rounded-md bg-surface-muted px-3 py-3 text-[13px] text-fg-muted">
            <LockKeyhole className="size-4" />{c.assignedTo?.name} está atendendo. Assuma a conversa para responder.
          </p>
        ) : windowClosed ? (
          <p className="rounded-md bg-warning-soft px-3 py-3 text-[13px] text-fg">
            Passou a janela de 24 h do Instagram: só dá para responder depois que a pessoa mandar uma nova mensagem.{c.contact.maskedPhone ? ' Ela tem telefone: dá para falar pelo WhatsApp.' : ''}
          </p>
        ) : c.status === 'closed' ? (
          <p className="rounded-md bg-surface-muted px-3 py-3 text-[13px] text-fg-muted">Conversa encerrada. Uma nova mensagem do paciente abre outra conversa.</p>
        ) : (
          <form className="relative flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            {canReply && <QuickReplyMenu ref={menu} text={text} vars={vars} onPick={pickReply} forceOpen={repliesOpen} onClose={() => { setRepliesOpen(false); if (text.startsWith('/')) setText(''); }} />}
            {canReply && (
              <Button type="button" variant="ghost" size="icon" className="size-10 shrink-0" aria-label="Respostas rápidas" title="Respostas rápidas (ou digite /)"
                onClick={() => { setRepliesOpen((v) => !v); input.current?.focus(); }}><Zap className="size-4" /></Button>
            )}
            <textarea ref={input} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} rows={Math.min(6, Math.max(1, text.split('\n').length))}
              aria-label="Mensagem" placeholder={mine ? 'Escreva uma mensagem…' : 'Escreva para responder — você assume a conversa ao enviar'}
              className="min-h-10 flex-1 resize-none rounded-md border border-border-strong bg-surface px-3 py-2.5 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
            <Button type="submit" size="icon" className="size-10" aria-label="Enviar" disabled={!text.trim() || !canWrite}><SendHorizontal className="size-4" /></Button>
          </form>
        )}
        {canWrite && <p className="mt-1.5 px-1 text-[11px] text-fg-subtle">Enter envia · Shift+Enter quebra a linha · / abre as respostas rápidas</p>}
      </div>
    </div>
  );
}
